// pages/ContactUsPage.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import API from '../api';
import { useNavigate } from 'react-router-dom';

const STORAGE_KEY = 'pharma_contact_messages';

const loadMessages = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

const saveMessages = (msgs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(msgs));
  } catch {}
};

/* ───── Toast Component ───── */
function Toast({ toast, onClose }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onClose, 400);
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast ${toast.type} ${exiting ? 'exit' : ''}`}>
      <div className="toast-icon">
        {toast.type === 'success' && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        {toast.type === 'error' && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        )}
        {toast.type === 'info' && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        )}
      </div>
      <div className="toast-content">
        <span className="toast-title">{toast.title}</span>
        <span className="toast-msg">{toast.message}</span>
      </div>
      <button className="toast-close" onClick={() => { setExiting(true); setTimeout(onClose, 400); }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      <div className="toast-progress">
        <div className={`toast-bar ${toast.type}`}></div>
      </div>
    </div>
  );
}

/* ───── Skeleton Loader ───── */
function MsgSkeleton() {
  return (
    <div className="msg-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="skel-card" style={{ animationDelay: `${i * 0.1}s` }}>
          <div className="skel-avatar shimmer"></div>
          <div className="skel-lines">
            <div className="skel-line w60 shimmer"></div>
            <div className="skel-line w40 shimmer"></div>
          </div>
          <div className="skel-badge shimmer"></div>
        </div>
      ))}
    </div>
  );
}

export default function ContactUsPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [ticketId, setTicketId] = useState(() => `PL-${Date.now().toString(36).toUpperCase()}`);
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(true);
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [msgsFilter, setMsgsFilter] = useState('all');
  const [msgsSearch, setMsgsSearch] = useState('');
  const [msgsPage, setMsgsPage] = useState(1);
  const [showHistory, setShowHistory] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const historyRef = useRef(null);
  const MSGS_PER_PAGE = 5;

  const addToast = (type, title, message) => {
    const id = Date.now();
    setToasts((t) => [...t, { id, type, title, message }]);
  };

  const removeToast = (id) => setToasts((t) => t.filter((x) => x.id !== id));

  useEffect(() => {
    setMsgsLoading(true);
    const timer = setTimeout(() => {
      setMessages(loadMessages());
      setMsgsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const nameValid = useMemo(() => name.trim().length >= 3 && name.trim().length <= 50, [name]);
  const emailValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()), [email]);
  const subjectValid = useMemo(() => subject.trim().length >= 3 && subject.trim().length <= 100, [subject]);
  const messageValid = useMemo(() => message.trim().length >= 10 && message.trim().length <= 1000, [message]);
  const formValid = nameValid && emailValid && subjectValid && messageValid;

  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const handleFocus = (field) => setFocusedField(field);
  const handleBlur = (field) => { setFocusedField(null); touch(field); };

  useEffect(() => {
    if (success && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (success && countdown === 0) {
      navigate('/');
    }
  }, [success, countdown, navigate]);

  const deleteMessage = (id) => {
    setDeletingId(id);
    setTimeout(() => {
      const updated = messages.filter((m) => m.id !== id);
      setMessages(updated);
      saveMessages(updated);
      setExpandedMsg(null);
      setDeletingId(null);
      addToast('info', 'Deleted', 'Message removed from history');
    }, 400);
  };

  const clearAllMessages = () => {
    if (!window.confirm('Clear all message history?')) return;
    setMessages([]);
    saveMessages([]);
    setExpandedMsg(null);
    setMsgsPage(1);
    addToast('info', 'Cleared', 'All messages removed');
  };

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, subject: true, message: true });
    if (!formValid || loading) return;

    setLoading(true);
    const newTicketId = `PL-${Date.now().toString(36).toUpperCase()}`;
    setTicketId(newTicketId);

    try {
      await API.post('/contacts', {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        subject: subject.trim(),
        message: message.trim(),
      });

      const newMsg = {
        id: newTicketId,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        subject: subject.trim(),
        message: message.trim(),
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      const updated = [newMsg, ...messages];
      setMessages(updated);
      saveMessages(updated);
      setSuccess(true);
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setTouched({});
    } catch (err) {
      addToast('error', 'Failed', err.response?.data?.message || 'Could not send message. Please try again.');
    }
    setLoading(false);
  };

  const filteredMessages = useMemo(() => {
    let list = [...messages];
    if (msgsFilter === 'replied') list = list.filter((m) => m.status === 'replied');
    if (msgsFilter === 'pending') list = list.filter((m) => m.status === 'pending' || !m.status);
    if (msgsSearch.trim()) {
      const q = msgsSearch.toLowerCase();
      list = list.filter(
        (m) =>
          (m.subject || '').toLowerCase().includes(q) ||
          (m.message || '').toLowerCase().includes(q) ||
          (m.name || '').toLowerCase().includes(q) ||
          (m.id || '').toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [messages, msgsFilter, msgsSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredMessages.length / MSGS_PER_PAGE));
  const paginatedMessages = filteredMessages.slice((msgsPage - 1) * MSGS_PER_PAGE, msgsPage * MSGS_PER_PAGE);
  const pendingCount = messages.filter((m) => m.status === 'pending' || !m.status).length;
  const repliedCount = messages.filter((m) => m.status === 'replied').length;

  const formatDate = (d) => {
    if (!d) return 'N/A';
    const date = new Date(d);
    const diff = Date.now() - date;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(diff / 86400000);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusInfo = (msg) => {
    if (msg.status === 'replied') return { label: 'Replied', cls: 'replied' };
    if (msg.status === 'in-progress') return { label: 'In Progress', cls: 'in-progress' };
    return { label: 'Pending', cls: 'pending' };
  };

  // ============ SUCCESS ============
  if (success) {
    return (
      <>
        <style>{styles}</style>
        <div className="toast-container">
          {toasts.map((t) => (<Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />))}
        </div>
        <div className="success-page">
          <div className="success-bg">
            <div className="success-bg-circle c1"></div>
            <div className="success-bg-circle c2"></div>
            <div className="success-bg-circle c3"></div>
          </div>
          <div className="success-container">
            <div className="success-particles">
              {[...Array(12)].map((_, i) => (<div key={i} className="particle" style={{ '--i': i }}></div>))}
            </div>
            <div className="success-icon-wrapper">
              <div className="success-icon-pulse"></div>
              <div className="success-icon-box">
                <svg viewBox="0 0 24 24" fill="none">
                  <path className="check-path" d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
            <h1 className="success-title">Message Sent Successfully!</h1>
            <p className="success-desc">Thank you for reaching out. Our team will get back to you within 24 hours.</p>
            <div className="ticket-card">
              <div className="ticket-header">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span>Submission Details</span>
              </div>
              <div className="ticket-body">
                <div className="ticket-row"><span className="ticket-label">Reference ID</span><span className="ticket-value mono">{ticketId}</span></div>
                <div className="ticket-row"><span className="ticket-label">Status</span><span className="ticket-status"><span className="status-dot"></span>Received</span></div>
                <div className="ticket-row"><span className="ticket-label">Response Time</span><span className="ticket-value">Within 24 hours</span></div>
              </div>
            </div>
            <div className="success-actions">
              <button className="btn-secondary" onClick={() => { setSuccess(false); setCountdown(5); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
                Send Another
              </button>
              <button className="btn-primary" onClick={() => navigate('/')}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                Back to Home
              </button>
            </div>
            <div className="redirect-section">
              <div className="redirect-info">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                <span>Redirecting in {countdown}s...</span>
              </div>
              <div className="redirect-bar"><div className="redirect-progress" style={{ width: `${(5 - countdown) * 20}%` }}></div></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============ MAIN ============
  return (
    <>
      <style>{styles}</style>
      <div className="toast-container">
        {toasts.map((t) => (<Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />))}
      </div>
      <div className="contact-page">
        <div className="bg-decorations">
          <div className="bg-circle bg-c1"></div>
          <div className="bg-circle bg-c2"></div>
          <div className="bg-circle bg-c3"></div>
        </div>

        <div className="contact-container">
          {/* LEFT */}
          <div className="contact-info">
            <div className="info-inner">
              <span className="info-badge">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
                Contact Us
              </span>
              <h1 className="info-title">Let's Start a Conversation</h1>
              <p className="info-subtitle">Have questions about our pharmacy management system? We're here to help you succeed.</p>

              <div className="contact-cards">
                {[
                  { icon: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></>, title: 'Email Us', text: 'support@pharmaledger.com' },
                  { icon: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />, title: 'Call Us', text: '+977 982-922-9020' },
                  { icon: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>, title: 'Visit Us', text: '123 Main Street, Birgunj, Nepal' },
                  { icon: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>, title: 'Working Hours', text: 'Mon - Fri: 9:00 AM - 6:00 PM' },
                ].map((c, i) => (
                  <div className="contact-card" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
                    <div className="card-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">{c.icon}</svg>
                    </div>
                    <div className="card-content"><h4>{c.title}</h4><p>{c.text}</p></div>
                  </div>
                ))}
              </div>

              <div className="social-section">
                <span className="social-label">Follow Us</span>
                <div className="social-links">
                  {[
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
                    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />,
                    <><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></>,
                    <><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></>,
                  ].map((icon, i) => (
                    <a href="#" className="social-btn" key={i} aria-label="social" style={{ animationDelay: `${i * 0.05}s` }}>
                      <svg viewBox="0 0 24 24" fill="currentColor">{icon}</svg>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT - FORM */}
          <div className="contact-form-wrapper">
            <div className="form-card">
              <div className="form-header">
                <h2>Send us a Message</h2>
                <p>Fill out the form and we'll respond within 24 hours</p>
              </div>

              <form onSubmit={submit} className="contact-form" noValidate>
                <div className="form-row">
                  {/* Name */}
                  <div className={`form-group ${focusedField === 'name' ? 'focused' : ''} ${touched.name ? (nameValid ? 'valid' : 'invalid') : ''}`}>
                    <label htmlFor="name">Full Name</label>
                    <div className="input-wrapper">
                      <input id="name" type="text" placeholder="John Doe" value={name}
                        onChange={(e) => setName(e.target.value.slice(0, 50))}
                        onFocus={() => handleFocus('name')} onBlur={() => handleBlur('name')} />
                      <span className={`input-check ${touched.name && nameValid ? 'show' : ''}`}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                      </span>
                    </div>
                    <span className={`error-msg ${touched.name && !nameValid ? 'show' : ''}`}>Name must be 3-50 characters</span>
                  </div>

                  {/* Email */}
                  <div className={`form-group ${focusedField === 'email' ? 'focused' : ''} ${touched.email ? (emailValid ? 'valid' : 'invalid') : ''}`}>
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <input id="email" type="email" placeholder="john@example.com" value={email}
                        onChange={(e) => setEmail(e.target.value.trim())}
                        onFocus={() => handleFocus('email')} onBlur={() => handleBlur('email')} />
                      <span className={`input-check ${touched.email && emailValid ? 'show' : ''}`}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                      </span>
                    </div>
                    <span className={`error-msg ${touched.email && !emailValid ? 'show' : ''}`}>Please enter a valid email</span>
                  </div>
                </div>

                {/* Subject */}
                <div className={`form-group ${focusedField === 'subject' ? 'focused' : ''} ${touched.subject ? (subjectValid ? 'valid' : 'invalid') : ''}`}>
                  <label htmlFor="subject">Subject</label>
                  <div className="input-wrapper">
                    <input id="subject" type="text" placeholder="How can we help you?" value={subject}
                      onChange={(e) => setSubject(e.target.value.slice(0, 100))}
                      onFocus={() => handleFocus('subject')} onBlur={() => handleBlur('subject')} />
                    <span className={`input-check ${touched.subject && subjectValid ? 'show' : ''}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                    </span>
                  </div>
                  <span className={`error-msg ${touched.subject && !subjectValid ? 'show' : ''}`}>Subject must be 3-100 characters</span>
                </div>

                {/* Message */}
                <div className={`form-group ${focusedField === 'message' ? 'focused' : ''} ${touched.message ? (messageValid ? 'valid' : 'invalid') : ''}`}>
                  <label htmlFor="message">Message<span className="char-count">{message.length}/1000</span></label>
                  <div className="input-wrapper">
                    <textarea id="message" placeholder="Tell us more about your inquiry..." rows={5} value={message}
                      onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                      onFocus={() => handleFocus('message')} onBlur={() => handleBlur('message')} />
                  </div>
                  <span className={`error-msg ${touched.message && !messageValid ? 'show' : ''}`}>Message must be 10-1000 characters</span>
                </div>

                <button type="submit" className={`submit-btn ${formValid ? 'enabled' : ''}`} disabled={loading || !formValid}>
                  {loading ? (<><span className="spinner"></span>Sending...</>) : (
                    <>Send Message<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg></>
                  )}
                </button>

                <p className="form-footer">By submitting, you agree to our <a href="/privacy">Privacy Policy</a> and <a href="/terms">Terms of Service</a>.</p>
              </form>
            </div>
          </div>
        </div>

        {/* ============ MESSAGE HISTORY ============ */}
        <div className="msg-history-section" ref={historyRef}>
          <div className="msg-history-header">
            <div className="msg-history-title-row">
              <div className="msg-history-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div>
                <h3>Your Message History</h3>
                <p>{messages.length} message{messages.length !== 1 ? 's' : ''} submitted</p>
              </div>
              <div className="msg-header-actions">
                {messages.length > 0 && (
                  <button className="msg-clear-btn" onClick={clearAllMessages}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    Clear All
                  </button>
                )}
                <button className="msg-toggle-btn" onClick={() => setShowHistory(!showHistory)} aria-label="Toggle history">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ transform: showHistory ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={`msg-toolbar-wrap ${showHistory && messages.length > 0 ? 'open' : ''}`}>
              <div className="msg-toolbar">
                <div className="msg-search-box">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  <input type="text" placeholder="Search messages..." value={msgsSearch}
                    onChange={(e) => { setMsgsSearch(e.target.value); setMsgsPage(1); }} />
                  {msgsSearch && (
                    <button className="msg-search-clear" onClick={() => { setMsgsSearch(''); setMsgsPage(1); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  )}
                </div>
                <div className="msg-filter-tabs">
                  {[
                    { key: 'all', label: 'All', count: messages.length },
                    { key: 'pending', label: 'Pending', count: pendingCount },
                    { key: 'replied', label: 'Replied', count: repliedCount },
                  ].map((f) => (
                    <button key={f.key} className={`msg-filter-tab ${msgsFilter === f.key ? 'active' : ''}`}
                      onClick={() => { setMsgsFilter(f.key); setMsgsPage(1); }}>
                      {f.label}<span className="tab-count">{f.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className={`msg-list-wrap ${showHistory ? 'open' : ''}`}>
            <div className="msg-list">
              {msgsLoading ? (
                <MsgSkeleton />
              ) : messages.length === 0 ? (
                <div className="msg-empty">
                  <div className="msg-empty-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      <line x1="9" y1="10" x2="15" y2="10" />
                    </svg>
                  </div>
                  <h4>No messages yet</h4>
                  <p>Messages you send will appear here for tracking.</p>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="msg-empty">
                  <div className="msg-empty-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  </div>
                  <h4>No results found</h4>
                  <p>Try adjusting your search or filter.</p>
                </div>
              ) : (
                <>
                  {paginatedMessages.map((msg, idx) => {
                    const status = getStatusInfo(msg);
                    const isExpanded = expandedMsg === msg.id;
                    const isDeleting = deletingId === msg.id;
                    return (
                      <div key={msg.id}
                        className={`msg-card ${isExpanded ? 'expanded' : ''} ${isDeleting ? 'deleting' : ''}`}
                        style={{ animationDelay: `${idx * 0.06}s` }}>
                        <div className="msg-card-top" onClick={() => setExpandedMsg(isExpanded ? null : msg.id)}>
                          <div className="msg-card-left">
                            <div className="msg-avatar">{(msg.name || 'U').charAt(0).toUpperCase()}</div>
                            <div className="msg-meta">
                              <h4 className="msg-subject">{msg.subject || 'No Subject'}</h4>
                              <div className="msg-info-row">
                                <span className="msg-sender">{msg.name}</span>
                                <span className="msg-dot">·</span>
                                <span className="msg-date">{formatDate(msg.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="msg-card-right">
                            <span className={`msg-status-badge ${status.cls}`}>
                              <span className="status-indicator"></span>{status.label}
                            </span>
                            <button className={`msg-expand-btn ${isExpanded ? 'open' : ''}`} aria-label="Toggle">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                            </button>
                          </div>
                        </div>

                        <div className={`msg-card-body-wrap ${isExpanded ? 'open' : ''}`}>
                          <div className="msg-card-body">
                            <div className="msg-detail-grid">
                              {[
                                { icon: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>, label: 'Name', value: msg.name },
                                { icon: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></>, label: 'Email', value: msg.email },
                                { icon: <><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /></>, label: 'Reference', value: msg.id, cls: 'mono' },
                                { icon: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>, label: 'Submitted', value: new Date(msg.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) },
                              ].map((d, i) => (
                                <div className="msg-detail-item" key={i}>
                                  <span className="msg-detail-label">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{d.icon}</svg>
                                    {d.label}
                                  </span>
                                  <span className={`msg-detail-value ${d.cls || ''}`}>{d.value}</span>
                                </div>
                              ))}
                            </div>
                            <div className="msg-body-content">
                              <span className="msg-body-label">Message</span>
                              <p>{msg.message}</p>
                            </div>
                            {msg.reply && (
                              <div className="msg-reply-box">
                                <div className="msg-reply-header">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>
                                  <span>Admin Reply</span>
                                </div>
                                <p>{msg.reply}</p>
                              </div>
                            )}
                            <div className="msg-card-actions">
                              <button className="msg-delete-btn" onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                Delete
                              </button>
                              <button className="msg-copy-btn" onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(msg.id);
                                addToast('success', 'Copied!', 'Reference ID copied to clipboard');
                              }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                Copy ID
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {totalPages > 1 && (
                    <div className="msg-pagination">
                      <button className="msg-page-btn" disabled={msgsPage <= 1} onClick={() => setMsgsPage((p) => p - 1)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>Prev
                      </button>
                      <div className="msg-page-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                          <button key={n} className={`msg-page-num ${msgsPage === n ? 'active' : ''}`} onClick={() => setMsgsPage(n)}>{n}</button>
                        ))}
                      </div>
                      <button className="msg-page-btn" disabled={msgsPage >= totalPages} onClick={() => setMsgsPage((p) => p + 1)}>
                        Next<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="faq-section">
          <h3 className="faq-title">Frequently Asked Questions</h3>
          <div className="faq-grid">
            {[
              { icon: <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></>, q: 'How quickly will I get a response?', a: 'We typically respond within 24 hours during business days.' },
              { icon: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>, q: 'Is my information secure?', a: 'Yes, all data is encrypted and we never share your information.' },
              { icon: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>, q: 'Can I request a demo?', a: "Absolutely! Just mention it in your message and we'll set it up." },
            ].map((f, i) => (
              <div className="faq-card" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="faq-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">{f.icon}</svg></div>
                <h4>{f.q}</h4>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ============ STYLES ============
const styles = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ═══ TOAST ═══ */
.toast-container {
  position: fixed; top: 24px; right: 24px; z-index: 9999;
  display: flex; flex-direction: column; gap: 10px; pointer-events: none;
}
.toast {
  pointer-events: all;
  display: flex; align-items: flex-start; gap: 12px;
  background: #fff; padding: 16px 18px; border-radius: 14px;
  box-shadow: 0 20px 50px rgba(0,0,0,0.15);
  border-left: 4px solid #667eea;
  min-width: 320px; max-width: 420px;
  animation: toastIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative; overflow: hidden;
}
.toast.exit { animation: toastOut 0.4s ease forwards; }
.toast.success { border-left-color: #10b981; }
.toast.error { border-left-color: #ef4444; }
.toast.info { border-left-color: #667eea; }

@keyframes toastIn {
  from { opacity: 0; transform: translateX(100px) scale(0.8); }
  to { opacity: 1; transform: translateX(0) scale(1); }
}
@keyframes toastOut {
  to { opacity: 0; transform: translateX(100px) scale(0.8); }
}

.toast-icon {
  width: 28px; height: 28px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.toast.success .toast-icon { background: #d1fae5; color: #10b981; }
.toast.error .toast-icon { background: #fee2e2; color: #ef4444; }
.toast.info .toast-icon { background: #e0e7ff; color: #667eea; }
.toast-icon svg { width: 16px; height: 16px; }

.toast-content { flex: 1; min-width: 0; }
.toast-title { display: block; font-size: 14px; font-weight: 700; color: #1a1a2e; }
.toast-msg { display: block; font-size: 13px; color: #64748b; margin-top: 2px; }

.toast-close {
  background: none; border: none; color: #94a3b8; cursor: pointer;
  padding: 4px; border-radius: 6px; transition: all 0.2s;
}
.toast-close:hover { background: #f1f5f9; color: #475569; }
.toast-close svg { width: 14px; height: 14px; }

.toast-progress { position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: #f1f5f9; }
.toast-bar { height: 100%; border-radius: 0 0 0 3px; animation: toastTimer 3s linear forwards; }
.toast-bar.success { background: #10b981; }
.toast-bar.error { background: #ef4444; }
.toast-bar.info { background: #667eea; }
@keyframes toastTimer { from { width: 100%; } to { width: 0; } }

/* ═══ SKELETON ═══ */
.msg-skeleton { display: flex; flex-direction: column; gap: 12px; padding: 8px 0; }
.skel-card {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 20px; background: #f8f9fa; border-radius: 14px;
  animation: fadeInUp 0.4s ease both;
}
.skel-avatar { width: 42px; height: 42px; border-radius: 12px; background: #e5e7eb; flex-shrink: 0; }
.skel-lines { flex: 1; display: flex; flex-direction: column; gap: 8px; }
.skel-line { height: 12px; border-radius: 6px; background: #e5e7eb; }
.skel-line.w60 { width: 60%; }
.skel-line.w40 { width: 40%; }
.skel-badge { width: 70px; height: 28px; border-radius: 20px; background: #e5e7eb; }

.shimmer {
  background: linear-gradient(90deg, #e5e7eb 25%, #f1f3f5 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite ease-in-out;
}
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ═══ PAGE ═══ */
.contact-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  position: relative; overflow-x: hidden; padding: 60px 24px;
}

.bg-decorations { position: fixed; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; z-index: 0; }
.bg-circle { position: absolute; border-radius: 50%; background: linear-gradient(135deg, rgba(102,126,234,0.08), rgba(118,75,162,0.08)); }
.bg-c1 { width: 600px; height: 600px; top: -200px; right: -150px; animation: floatSlow 25s ease-in-out infinite; }
.bg-c2 { width: 400px; height: 400px; bottom: -100px; left: -100px; animation: floatSlow 20s ease-in-out infinite reverse; }
.bg-c3 { width: 300px; height: 300px; top: 50%; left: 30%; animation: floatSlow 30s ease-in-out infinite; }
@keyframes floatSlow { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-30px) rotate(5deg); } }

.contact-container { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1.1fr; gap: 40px; position: relative; z-index: 1; }

/* LEFT */
.contact-info { display: flex; align-items: center; }
.info-inner { width: 100%; }
.info-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff; padding: 10px 20px; border-radius: 50px;
  font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  margin-bottom: 24px; box-shadow: 0 4px 15px rgba(102,126,234,0.4);
  animation: fadeInUp 0.5s ease;
}
.info-badge svg { width: 16px; height: 16px; }
.info-title { font-size: 42px; font-weight: 800; color: #1a1a2e; line-height: 1.2; margin-bottom: 16px; animation: fadeInUp 0.6s ease; }
.info-subtitle { font-size: 16px; color: #64748b; line-height: 1.7; margin-bottom: 40px; animation: fadeInUp 0.7s ease; }

@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

.contact-cards { display: flex; flex-direction: column; gap: 16px; margin-bottom: 40px; }
.contact-card {
  display: flex; align-items: center; gap: 16px;
  background: #fff; padding: 18px 20px; border-radius: 14px;
  border: 1px solid #e8e8e8;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer; animation: fadeInUp 0.5s ease both;
}
.contact-card:hover { transform: translateX(8px); border-color: #667eea; box-shadow: 0 8px 30px rgba(102,126,234,0.15); }
.card-icon {
  width: 50px; height: 50px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.contact-card:hover .card-icon { transform: scale(1.1) rotate(-5deg); }
.card-icon svg { width: 24px; height: 24px; color: #fff; }
.card-content h4 { font-size: 15px; font-weight: 600; color: #1a1a2e; margin-bottom: 4px; }
.card-content p { font-size: 14px; color: #64748b; }

.social-section { display: flex; align-items: center; gap: 16px; animation: fadeInUp 0.8s ease; }
.social-label { font-size: 14px; font-weight: 600; color: #64748b; }
.social-links { display: flex; gap: 10px; }
.social-btn {
  width: 42px; height: 42px; background: #fff; border: 1px solid #e8e8e8;
  border-radius: 10px; display: flex; align-items: center; justify-content: center;
  color: #64748b; text-decoration: none;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.5s ease both;
}
.social-btn svg { width: 18px; height: 18px; }
.social-btn:hover { background: linear-gradient(135deg, #667eea, #764ba2); border-color: transparent; color: #fff; transform: translateY(-4px) scale(1.05); box-shadow: 0 8px 20px rgba(102,126,234,0.4); }

/* RIGHT */
.contact-form-wrapper { display: flex; align-items: center; }
.form-card {
  background: #fff; border-radius: 24px; padding: 44px; width: 100%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.08); border: 1px solid rgba(255,255,255,0.9);
  animation: fadeInUp 0.5s ease;
}
.form-header { margin-bottom: 32px; text-align: center; }
.form-header h2 { font-size: 26px; font-weight: 700; color: #1a1a2e; margin-bottom: 8px; }
.form-header p { font-size: 14px; color: #64748b; }

.contact-form { display: flex; flex-direction: column; gap: 22px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.form-group { display: flex; flex-direction: column; gap: 8px; }
.form-group label { display: flex; justify-content: space-between; align-items: center; font-size: 14px; font-weight: 600; color: #374151; transition: color 0.3s ease; }
.char-count { font-size: 12px; font-weight: 400; color: #9ca3af; transition: color 0.3s ease; }
.input-wrapper { position: relative; }

.form-group input, .form-group textarea {
  width: 100%; padding: 14px 18px; font-size: 15px; font-family: inherit;
  color: #1a1a2e; background: #f8f9fa; border: 2px solid #e5e7eb;
  border-radius: 12px; outline: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.form-group textarea { resize: vertical; min-height: 130px; }
.form-group input::placeholder, .form-group textarea::placeholder { color: #9ca3af; }
.form-group input:hover, .form-group textarea:hover { border-color: #d1d5db; }

.form-group.focused input, .form-group.focused textarea {
  background: #fff; border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102,126,234,0.1);
  transform: translateY(-1px);
}
.form-group.focused label { color: #667eea; }
.form-group.invalid input, .form-group.invalid textarea { border-color: #ef4444; background: #fef2f2; }
.form-group.invalid.focused input, .form-group.invalid.focused textarea { box-shadow: 0 0 0 4px rgba(239,68,68,0.1); }
.form-group.valid input, .form-group.valid textarea { border-color: #10b981; background: #f0fdf4; }
.form-group.valid.focused input, .form-group.valid.focused textarea { box-shadow: 0 0 0 4px rgba(16,185,129,0.1); }

.input-check {
  position: absolute; right: 14px; top: 50%; transform: translateY(-50%) scale(0);
  width: 20px; height: 20px; color: #10b981;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.input-check.show { transform: translateY(-50%) scale(1); }
.input-check svg { width: 100%; height: 100%; }

.error-msg {
  font-size: 13px; color: #ef4444;
  max-height: 0; overflow: hidden; opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.error-msg.show { max-height: 24px; opacity: 1; }

.submit-btn {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; padding: 16px 28px; font-size: 16px; font-weight: 600;
  font-family: inherit; color: #fff; background: #9ca3b8;
  border: none; border-radius: 12px; cursor: not-allowed;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); margin-top: 8px;
  position: relative; overflow: hidden;
}
.submit-btn svg { width: 20px; height: 20px; transition: transform 0.3s ease; }
.submit-btn.enabled { background: linear-gradient(135deg, #667eea, #764ba2); cursor: pointer; box-shadow: 0 8px 25px rgba(102,126,234,0.4); }
.submit-btn.enabled:hover { transform: translateY(-2px); box-shadow: 0 12px 35px rgba(102,126,234,0.5); }
.submit-btn.enabled:hover svg { transform: translateX(4px) translateY(-2px); }
.submit-btn.enabled:active { transform: translateY(0) scale(0.98); }

.submit-btn.enabled::after {
  content: ''; position: absolute; top: 0; left: -100%; width: 100%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
  transition: left 0.5s ease;
}
.submit-btn.enabled:hover::after { left: 100%; }

.spinner { width: 20px; height: 20px; border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.form-footer { font-size: 13px; color: #9ca3af; text-align: center; margin-top: 8px; }
.form-footer a { color: #667eea; text-decoration: none; font-weight: 500; transition: color 0.2s; }
.form-footer a:hover { text-decoration: underline; }

/* ═══ MESSAGE HISTORY ═══ */
.msg-history-section {
  max-width: 1200px; margin: 60px auto 0; position: relative; z-index: 1;
  background: #fff; border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.06); border: 1px solid #e8e8e8; overflow: hidden;
  animation: fadeInUp 0.6s ease;
}

.msg-history-header { padding: 28px 32px 24px; border-bottom: 1px solid #f1f3f5; }
.msg-history-title-row { display: flex; align-items: center; gap: 16px; margin-bottom: 4px; }
.msg-history-icon {
  width: 50px; height: 50px;
  background: linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1));
  border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.msg-history-icon svg { width: 24px; height: 24px; color: #667eea; }
.msg-history-title-row h3 { font-size: 20px; font-weight: 700; color: #1a1a2e; margin-bottom: 2px; }
.msg-history-title-row p { font-size: 13px; color: #94a3b8; }

.msg-header-actions { display: flex; align-items: center; gap: 10px; margin-left: auto; }
.msg-clear-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; font-size: 13px; font-weight: 500; font-family: inherit;
  background: #fef2f2; color: #dc2626; border: 1px solid #fecaca;
  border-radius: 10px; cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-clear-btn svg { width: 15px; height: 15px; }
.msg-clear-btn:hover { background: #fee2e2; border-color: #f87171; transform: scale(1.02); }

.msg-toggle-btn {
  width: 36px; height: 36px; background: #f1f5f9; border: none; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #64748b; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-toggle-btn:hover { background: #e2e8f0; }
.msg-toggle-btn svg { width: 18px; height: 18px; transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1); }

/* Smooth Toolbar Collapse */
.msg-toolbar-wrap {
  max-height: 0; overflow: hidden; opacity: 0;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease, margin 0.3s ease;
  margin-top: 0;
}
.msg-toolbar-wrap.open { max-height: 100px; opacity: 1; margin-top: 20px; }

.msg-toolbar { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }

.msg-search-box {
  display: flex; align-items: center; gap: 10px;
  background: #f8f9fa; border: 2px solid #e5e7eb; border-radius: 12px;
  padding: 10px 16px; flex: 1; min-width: 200px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-search-box:focus-within { border-color: #667eea; background: #fff; box-shadow: 0 0 0 4px rgba(102,126,234,0.1); }
.msg-search-box svg { width: 18px; height: 18px; color: #94a3b8; flex-shrink: 0; transition: color 0.3s; }
.msg-search-box:focus-within svg { color: #667eea; }
.msg-search-box input { border: none; background: transparent; outline: none; font-size: 14px; color: #1a1a2e; font-family: inherit; width: 100%; }
.msg-search-box input::placeholder { color: #94a3b8; }

.msg-search-clear { background: none; border: none; padding: 2px; cursor: pointer; color: #94a3b8; display: flex; transition: all 0.2s; }
.msg-search-clear:hover { color: #ef4444; transform: rotate(90deg); }
.msg-search-clear svg { width: 16px; height: 16px; }

.msg-filter-tabs { display: flex; gap: 4px; background: #f1f5f9; border-radius: 10px; padding: 4px; }
.msg-filter-tab {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 14px; font-size: 13px; font-weight: 600; font-family: inherit;
  background: transparent; border: none; border-radius: 8px;
  color: #64748b; cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-filter-tab.active { background: #fff; color: #667eea; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.msg-filter-tab:hover:not(.active) { color: #475569; background: rgba(255,255,255,0.5); }

.tab-count {
  background: rgba(102,126,234,0.1); color: #667eea;
  font-size: 11px; padding: 2px 7px; border-radius: 20px;
  transition: all 0.3s;
}
.msg-filter-tab.active .tab-count { background: rgba(102,126,234,0.15); }

/* Smooth List Collapse */
.msg-list-wrap {
  max-height: 0; overflow: hidden; opacity: 0;
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease;
}
.msg-list-wrap.open { max-height: 2000px; opacity: 1; }

.msg-list { padding: 24px 32px 28px; display: flex; flex-direction: column; gap: 12px; }

.msg-empty { text-align: center; padding: 50px 20px; animation: fadeInUp 0.4s ease; }
.msg-empty-icon {
  width: 70px; height: 70px;
  background: linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1));
  border-radius: 20px; display: flex; align-items: center; justify-content: center;
  margin: 0 auto 18px;
}
.msg-empty-icon svg { width: 30px; height: 30px; color: #94a3b8; }
.msg-empty h4 { font-size: 17px; font-weight: 600; color: #475569; margin-bottom: 8px; }
.msg-empty p { font-size: 14px; color: #94a3b8; max-width: 340px; margin: 0 auto; line-height: 1.6; }

.msg-card {
  background: #f8f9fa; border: 2px solid #e5e7eb; border-radius: 16px;
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.4s ease both;
}
.msg-card:hover { border-color: #d1d5db; box-shadow: 0 4px 20px rgba(0,0,0,0.04); transform: translateY(-1px); }
.msg-card.expanded { border-color: #667eea; background: #fff; box-shadow: 0 8px 30px rgba(102,126,234,0.1); transform: translateY(-2px); }
.msg-card.deleting { animation: cardDelete 0.4s ease forwards; }

@keyframes cardDelete {
  to { opacity: 0; transform: translateX(60px) scale(0.95); max-height: 0; margin: 0; padding: 0; border-width: 0; }
}

.msg-card-top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; gap: 16px; cursor: pointer;
  transition: background 0.2s;
}
.msg-card-top:hover { background: rgba(102,126,234,0.02); }

.msg-card-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 0; }
.msg-avatar {
  width: 42px; height: 42px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 12px; display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 17px; flex-shrink: 0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-card:hover .msg-avatar { transform: scale(1.05); }

.msg-meta { min-width: 0; flex: 1; }
.msg-subject { font-size: 15px; font-weight: 600; color: #1a1a2e; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 3px; transition: color 0.2s; }
.msg-card.expanded .msg-subject { color: #667eea; }
.msg-info-row { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #94a3b8; }
.msg-dot { font-size: 8px; }

.msg-card-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.msg-status-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 12px; border-radius: 50px; font-size: 12px; font-weight: 600;
  transition: all 0.3s;
}
.msg-status-badge .status-indicator { width: 7px; height: 7px; border-radius: 50%; }
.msg-status-badge.pending { background: #fef3c7; color: #b45309; }
.msg-status-badge.pending .status-indicator { background: #f59e0b; animation: blink 1.5s ease-in-out infinite; }
.msg-status-badge.replied { background: #d1fae5; color: #065f46; }
.msg-status-badge.replied .status-indicator { background: #10b981; }
.msg-status-badge.in-progress { background: #dbeafe; color: #1e40af; }
.msg-status-badge.in-progress .status-indicator { background: #3b82f6; animation: blink 1.5s ease-in-out infinite; }
@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

.msg-expand-btn {
  width: 30px; height: 30px; background: transparent; border: none; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #94a3b8; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-expand-btn:hover { background: #e5e7eb; color: #475569; }
.msg-expand-btn svg { width: 18px; height: 18px; transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
.msg-expand-btn.open svg { transform: rotate(180deg); }

/* Smooth card body expand */
.msg-card-body-wrap {
  max-height: 0; overflow: hidden; opacity: 0;
  transition: max-height 0.45s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease;
}
.msg-card-body-wrap.open { max-height: 800px; opacity: 1; }

.msg-card-body { padding: 0 20px 20px; border-top: 1px solid #e5e7eb; }

.msg-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding: 16px 0; }
.msg-detail-item { display: flex; flex-direction: column; gap: 3px; }
.msg-detail-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: #94a3b8; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.msg-detail-label svg { width: 13px; height: 13px; }
.msg-detail-value { font-size: 14px; font-weight: 600; color: #1a1a2e; word-break: break-all; }
.msg-detail-value.mono { font-family: 'SF Mono', monospace; color: #667eea; font-size: 13px; }

.msg-body-content { background: #f8f9fa; border-radius: 12px; padding: 16px; margin-top: 4px; }
.msg-body-label { display: block; font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
.msg-body-content p { font-size: 14px; color: #374151; line-height: 1.7; white-space: pre-wrap; word-break: break-word; }

.msg-reply-box {
  background: linear-gradient(135deg, rgba(102,126,234,0.05), rgba(118,75,162,0.05));
  border: 1px solid rgba(102,126,234,0.15);
  border-radius: 12px; padding: 16px; margin-top: 12px;
}
.msg-reply-header { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #667eea; margin-bottom: 8px; }
.msg-reply-header svg { width: 16px; height: 16px; }
.msg-reply-box p { font-size: 14px; color: #374151; line-height: 1.7; white-space: pre-wrap; }

.msg-card-actions { display: flex; gap: 10px; margin-top: 14px; padding-top: 14px; border-top: 1px solid #f1f3f5; }
.msg-delete-btn, .msg-copy-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; font-size: 13px; font-weight: 500; font-family: inherit;
  border-radius: 8px; cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border: none;
}
.msg-delete-btn svg, .msg-copy-btn svg { width: 14px; height: 14px; }
.msg-delete-btn { background: #fef2f2; color: #dc2626; }
.msg-delete-btn:hover { background: #fee2e2; transform: scale(1.03); }
.msg-copy-btn { background: #f0f9ff; color: #0284c7; }
.msg-copy-btn:hover { background: #e0f2fe; transform: scale(1.03); }

.msg-pagination { display: flex; align-items: center; justify-content: center; gap: 8px; padding-top: 16px; border-top: 1px solid #f1f3f5; }
.msg-page-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; font-size: 13px; font-weight: 600; font-family: inherit;
  background: #f1f5f9; border: none; border-radius: 10px;
  color: #475569; cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-page-btn:hover:not(:disabled) { background: #e2e8f0; color: #667eea; transform: translateY(-1px); }
.msg-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.msg-page-btn svg { width: 16px; height: 16px; }
.msg-page-numbers { display: flex; gap: 4px; }
.msg-page-num {
  width: 34px; height: 34px; display: flex; align-items: center; justify-content: center;
  font-size: 14px; font-weight: 600; font-family: inherit;
  background: transparent; border: none; border-radius: 8px;
  color: #64748b; cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.msg-page-num:hover { background: #f1f5f9; transform: translateY(-1px); }
.msg-page-num.active { background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; box-shadow: 0 4px 12px rgba(102,126,234,0.35); transform: scale(1.05); }

/* ═══ FAQ ═══ */
.faq-section { max-width: 1200px; margin: 80px auto 0; position: relative; z-index: 1; }
.faq-title { font-size: 28px; font-weight: 700; color: #1a1a2e; text-align: center; margin-bottom: 40px; }
.faq-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.faq-card {
  background: #fff; border: 1px solid #e8e8e8; border-radius: 18px;
  padding: 32px; text-align: center;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fadeInUp 0.5s ease both;
}
.faq-card:hover { transform: translateY(-6px); box-shadow: 0 15px 40px rgba(0,0,0,0.08); border-color: #667eea; }
.faq-icon {
  width: 56px; height: 56px;
  background: linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1));
  border-radius: 14px; display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px; transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.faq-card:hover .faq-icon { transform: scale(1.1) rotate(-5deg); }
.faq-icon svg { width: 26px; height: 26px; color: #667eea; }
.faq-card h4 { font-size: 17px; font-weight: 600; color: #1a1a2e; margin-bottom: 10px; }
.faq-card p { font-size: 14px; color: #64748b; line-height: 1.6; }

/* ═══ SUCCESS ═══ */
.success-page {
  min-height: 100vh; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  padding: 24px; position: relative; overflow: hidden;
}
.success-bg { position: fixed; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; }
.success-bg-circle { position: absolute; border-radius: 50%; background: linear-gradient(135deg, rgba(102,126,234,0.08), rgba(118,75,162,0.08)); }
.success-bg-circle.c1 { width: 500px; height: 500px; top: -200px; right: -100px; }
.success-bg-circle.c2 { width: 400px; height: 400px; bottom: -150px; left: -100px; }
.success-bg-circle.c3 { width: 300px; height: 300px; top: 40%; left: 20%; }

.success-container {
  background: #fff; border-radius: 24px; padding: 50px;
  max-width: 500px; width: 100%; text-align: center;
  box-shadow: 0 25px 80px rgba(0,0,0,0.1);
  position: relative; z-index: 1;
  animation: successIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes successIn { from { opacity: 0; transform: translateY(40px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }

.success-particles { position: absolute; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; overflow: hidden; border-radius: 24px; }
.particle {
  position: absolute; width: 8px; height: 8px; border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  top: 50%; left: 50%; opacity: 0;
  animation: particleBurst 1s ease-out calc(var(--i) * 0.05s + 0.3s) forwards;
}
@keyframes particleBurst {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% {
    transform: translate(
      calc(cos(calc(var(--i) * 30deg)) * 120px),
      calc(sin(calc(var(--i) * 30deg)) * 120px)
    ) scale(0);
    opacity: 0;
  }
}

.success-icon-wrapper { position: relative; width: 90px; height: 90px; margin: 0 auto 28px; }
.success-icon-pulse {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(102,126,234,0.2), rgba(118,75,162,0.2));
  border-radius: 50%; animation: pulse 2s ease-out infinite;
}
@keyframes pulse { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(1.6); opacity: 0; } }

.success-icon-box {
  position: relative; width: 90px; height: 90px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 50%; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 15px 40px rgba(102,126,234,0.4);
  animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
}
@keyframes popIn { from { transform: scale(0) rotate(-180deg); } to { transform: scale(1) rotate(0deg); } }
.success-icon-box svg { width: 40px; height: 40px; }
.check-path { stroke-dasharray: 30; stroke-dashoffset: 30; animation: drawCheck 0.5s ease 0.6s forwards; }
@keyframes drawCheck { to { stroke-dashoffset: 0; } }

.success-title { font-size: 26px; font-weight: 700; color: #1a1a2e; margin-bottom: 12px; animation: fadeInUp 0.5s ease 0.3s both; }
.success-desc { font-size: 15px; color: #64748b; line-height: 1.7; margin-bottom: 32px; animation: fadeInUp 0.5s ease 0.4s both; }

.ticket-card { background: #f8f9fa; border: 1px solid #e5e7eb; border-radius: 16px; overflow: hidden; margin-bottom: 28px; text-align: left; animation: fadeInUp 0.5s ease 0.5s both; }
.ticket-header { display: flex; align-items: center; gap: 10px; padding: 16px 20px; background: linear-gradient(135deg, rgba(102,126,234,0.08), rgba(118,75,162,0.08)); border-bottom: 1px solid #e5e7eb; font-size: 14px; font-weight: 600; color: #667eea; }
.ticket-header svg { width: 18px; height: 18px; }
.ticket-body { padding: 8px 20px; }
.ticket-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 0; border-bottom: 1px solid #f1f3f5; }
.ticket-row:last-child { border-bottom: none; }
.ticket-label { font-size: 13px; color: #64748b; }
.ticket-value { font-size: 14px; font-weight: 600; color: #1a1a2e; }
.ticket-value.mono { font-family: 'SF Mono', monospace; color: #667eea; }
.ticket-status { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #10b981; }
.status-dot { width: 8px; height: 8px; background: #10b981; border-radius: 50%; animation: blink 1.5s ease-in-out infinite; }

.success-actions { display: flex; gap: 12px; margin-bottom: 28px; animation: fadeInUp 0.5s ease 0.6s both; }
.btn-secondary, .btn-primary {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 14px 20px; font-size: 14px; font-weight: 600; font-family: inherit;
  border-radius: 12px; cursor: pointer;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); border: none;
}
.btn-secondary svg, .btn-primary svg { width: 18px; height: 18px; }
.btn-secondary { background: #f1f5f9; color: #475569; }
.btn-secondary:hover { background: #e2e8f0; transform: translateY(-2px); }
.btn-primary { background: linear-gradient(135deg, #667eea, #764ba2); color: #fff; box-shadow: 0 8px 25px rgba(102,126,234,0.35); position: relative; overflow: hidden; }
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 35px rgba(102,126,234,0.45); }
.btn-primary::after {
  content: ''; position: absolute; top: 0; left: -100%; width: 100%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
  transition: left 0.5s;
}
.btn-primary:hover::after { left: 100%; }

.redirect-section { padding-top: 24px; border-top: 1px solid #e5e7eb; animation: fadeInUp 0.5s ease 0.7s both; }
.redirect-info { display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 13px; color: #94a3b8; margin-bottom: 14px; }
.redirect-info svg { width: 16px; height: 16px; }
.redirect-bar { height: 6px; background: #e5e7eb; border-radius: 3px; overflow: hidden; }
.redirect-progress { height: 100%; background: linear-gradient(135deg, #667eea, #764ba2); border-radius: 3px; transition: width 1s cubic-bezier(0.4, 0, 0.2, 1); }

/* ═══ RESPONSIVE ═══ */
@media (max-width: 1024px) {
  .contact-container { grid-template-columns: 1fr; gap: 48px; }
  .contact-info { order: 2; } .contact-form-wrapper { order: 1; }
  .info-title { font-size: 34px; }
  .faq-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .contact-page { padding: 40px 20px; }
  .form-card { padding: 32px 28px; border-radius: 20px; }
  .form-row { grid-template-columns: 1fr; }
  .info-title { font-size: 28px; }
  .faq-grid { grid-template-columns: 1fr; }
  .success-container { padding: 40px 28px; }
  .success-title { font-size: 22px; }
  .success-actions { flex-direction: column; }
  .msg-history-header { padding: 20px 20px 16px; }
  .msg-list { padding: 20px; }
  .msg-detail-grid { grid-template-columns: 1fr; }
  .msg-toolbar { gap: 12px; }
  .msg-history-title-row { flex-wrap: wrap; }
  .toast { min-width: 280px; max-width: 90vw; }
}
@media (max-width: 480px) {
  .contact-page { padding: 32px 16px; }
  .form-card { padding: 28px 22px; }
  .info-title { font-size: 24px; }
  .info-badge { font-size: 11px; padding: 8px 16px; }
  .social-section { flex-direction: column; align-items: flex-start; gap: 12px; }
  .success-container { padding: 32px 20px; }
  .msg-history-header { padding: 16px; }
  .msg-list { padding: 16px; }
  .msg-card-top { padding: 12px 14px; }
  .msg-card-body { padding: 0 14px 14px; }
  .msg-card-actions { flex-direction: column; }
  .toast-container { top: 12px; right: 12px; left: 12px; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}

.form-group input:focus-visible, .form-group textarea:focus-visible,
.submit-btn:focus-visible, .btn-primary:focus-visible, .btn-secondary:focus-visible,
.social-btn:focus-visible, .msg-filter-tab:focus-visible, .msg-page-btn:focus-visible,
.msg-page-num:focus-visible, .msg-search-box input:focus-visible,
.msg-expand-btn:focus-visible, .msg-delete-btn:focus-visible, .msg-copy-btn:focus-visible {
  outline: 3px solid #667eea; outline-offset: 2px;
}
`;